import type { ThemeConfig } from 'antd'
import { theme as antdTheme } from 'antd'

import type { TRadiusMode } from '@web/shared/local-storage/border-radius.storage'
import type { TTheme } from '@web/shared/local-storage/theme.storage'
import { getCssVariable } from '@web/shared/utils/get-css-variable.util'

export const getDesignTokensAntd = (
  themeName: TTheme,
  radiusMode: TRadiusMode
): ThemeConfig => {
  const selectColor = getCssVariable('--color-bg-selected')
  const hoverColor = getCssVariable('--color-bg-hover')
  const bgFirstColor = getCssVariable('--color-bg-surface')
  const bgSecondColor = getCssVariable('--color-bg-elevated')
  const borderColor = getCssVariable('--color-border')
  const errorColor = getCssVariable('--color-danger')
  const primaryColor = getCssVariable('--color-text')
  const bgPageColor = getCssVariable('--color-bg-page')
  const accentColor = getCssVariable('--color-control-accent')
  const onAccentColor = getCssVariable('--color-control-on-accent')
  const mutedColor = getCssVariable('--color-muted')
  const hoverBorderColor = getCssVariable('--color-border-hover')
  const controlHeight = parseFloat(getCssVariable('--control-height'))
  const paddingInline = parseFloat(getCssVariable('--control-padding-inline'))
  const paddingBlock = parseFloat(getCssVariable('--control-padding-block'))
  const fontSize = parseFloat(getCssVariable('--font-size-base'))
  const lineHeight = parseFloat(getCssVariable('--control-line-height'))
  const inputTokens = {
    activeBorderColor: hoverBorderColor,
    hoverBorderColor,
    activeShadow: 'none',
    paddingBlock,
    paddingInline,
    inputFontSize: fontSize
  }
  // Значения по умолчанию на случай, если CSS-переменные ещё не применены к DOM
  const radiusFallback = radiusMode === 'rounded' ? 8 : 0
  const radiusSmFallback = radiusMode === 'rounded' ? 4 : 0
  const radiusLgFallback = radiusMode === 'rounded' ? 14 : 0
  const radiusSm = parseInt(getCssVariable('--radius-sm')) || radiusSmFallback
  const radiusValue =
    parseInt(getCssVariable('--radius-value')) || radiusFallback
  const radiusLg = parseInt(getCssVariable('--radius-lg')) || radiusLgFallback
  const spacePadding = getCssVariable('--space-padding')

  return {
    algorithm:
      themeName === 'dark'
        ? antdTheme.darkAlgorithm
        : antdTheme.defaultAlgorithm,
    token: {
      colorPrimary: accentColor,
      colorPrimaryHover: hoverBorderColor,
      colorPrimaryActive: accentColor,
      colorPrimaryBg: selectColor,
      colorPrimaryBgHover: hoverColor,
      colorText: primaryColor,
      colorTextLightSolid: onAccentColor,
      colorTextPlaceholder: getCssVariable('--color-control-placeholder'),
      colorTextDisabled: mutedColor,
      colorBgContainerDisabled: bgFirstColor,
      controlItemBgActive: selectColor,
      controlItemBgActiveHover: hoverColor,
      colorPrimaryBorder: borderColor,
      colorBorder: borderColor,
      colorBgBase: bgPageColor,
      colorTextBase: primaryColor,
      colorBgContainer: bgSecondColor,
      colorBgElevated: bgSecondColor,
      colorBgLayout: bgPageColor,
      borderRadiusSM: radiusSm,
      borderRadiusLG: radiusLg,
      borderRadius: radiusValue,
      fontFamily: 'Inter, Arial, Helvetica, sans-serif',
      padding: 4,
      controlItemBgHover: hoverColor,
      fontSize,
      colorLink: primaryColor,
      colorLinkHover: hoverBorderColor,
      colorLinkActive: accentColor,
      controlHeight,
      lineHeight,
      colorErrorText: errorColor,
      colorError: errorColor
    },
    components: {
      Checkbox: {
        colorWhite: onAccentColor,
        borderRadiusSM: radiusSm
      },
      Radio: {
        colorWhite: onAccentColor,
        buttonSolidCheckedColor: onAccentColor
      },
      Button: {
        borderRadius: radiusSm,
        primaryShadow: 'none',
        defaultColor: primaryColor,
        fontWeight: 700,
        defaultBorderColor: borderColor,
        defaultHoverBorderColor: primaryColor,
        defaultHoverColor: primaryColor,
        defaultHoverBg: hoverColor,
        defaultActiveBg: selectColor,
        defaultActiveBorderColor: primaryColor,
        defaultActiveColor: primaryColor
      },
      Tree: {
        nodeSelectedBg: selectColor,
        nodeHoverBg: hoverColor,
        titleHeight: 18,
        indentSize: 18
        // paddingXS: 2
      },
      TreeSelect: {
        nodeSelectedBg: selectColor,
        nodeHoverBg: hoverColor
      },
      Collapse: {
        headerBg: bgFirstColor,
        contentBg: bgSecondColor,
        colorBorder: borderColor,
        colorText: primaryColor,
        colorTextHeading: primaryColor,
        headerPadding: '4px 12px',
        contentPadding: spacePadding,
        borderlessContentPadding: 0,
        // В ghost-режиме вертикальный отступ задаётся через paddingSM.
        paddingSM: 0,
        borderRadiusLG: radiusSm
      },
      Table: {
        filterDropdownBg: bgSecondColor,
        filterDropdownMenuBg: bgSecondColor,
        headerFilterHoverBg: hoverColor,
        headerSortActiveBg: selectColor,
        headerSortHoverBg: hoverColor,
        rowSelectedBg: selectColor,
        rowSelectedHoverBg: selectColor,
        rowHoverBg: hoverColor,
        headerBg: bgFirstColor,
        headerSplitColor: borderColor,
        borderColor: borderColor,
        headerBorderRadius: radiusSm
      },
      Form: {
        labelRequiredMarkColor: errorColor,
        itemMarginBottom: 0,
        labelHeight: 28
      },
      Input: {
        ...inputTokens,
        borderRadius: radiusSm,
        colorBgContainer: bgSecondColor
      },
      InputNumber: {
        ...inputTokens,
        borderRadius: radiusSm,
        colorBgContainer: bgSecondColor
      },
      DatePicker: {
        ...inputTokens,
        borderRadius: radiusSm,
        borderRadiusLG: radiusSm,
        cellHoverBg: hoverColor,
        cellActiveWithRangeBg: selectColor,
        cellHoverWithRangeBg: hoverColor,
        cellRangeBorderColor: hoverBorderColor,
        cellBgDisabled: bgFirstColor
      },
      Menu: {
        itemBg: bgSecondColor,
        itemColor: primaryColor,
        itemHoverBg: hoverColor,
        itemHoverColor: primaryColor,
        itemSelectedBg: selectColor,
        itemSelectedColor: primaryColor,
        itemBorderRadius: radiusSm
      },
      Select: {
        borderRadius: radiusSm,
        borderRadiusLG: radiusSm,
        hoverBorderColor,
        activeBorderColor: hoverBorderColor,
        activeOutlineColor: 'transparent',
        selectorBg: bgSecondColor,
        optionSelectedBg: selectColor,
        optionSelectedColor: primaryColor,
        optionPadding: spacePadding,
        optionLineHeight: lineHeight,
        optionFontSize: fontSize,
        optionHeight: controlHeight,
        optionActiveBg: hoverColor
      },
      Modal: {
        borderRadius: radiusSm
      }
    }
  }
}
